import { db } from "../firebaseSetup";
import { doc, getDoc } from "firebase/firestore";
import { Logger } from "../utils/logger";
import { getLandingPageId } from "../context/landingPageContext";
import { getRecommendationId } from "../context/recommendationContext";
import { isPreviewMode } from "./previewUtils";

// Cache für LandingPage-Daten
const landingPageCache: Record<string, any> = {};

/**
 * Lädt LandingPage-Daten mit Cache
 */
async function loadLandingPageData(landingPageId: string): Promise<any | null> {
    if (landingPageCache[landingPageId]) return landingPageCache[landingPageId];

    try {
        const snap = await getDoc(doc(db, "landingPages", landingPageId));
        if (!snap.exists()) return null;

        const data = snap.data();
        landingPageCache[landingPageId] = data;
        return data;
    } catch (err) {
        Logger.error(`❌ Fehler beim Laden von LandingPage ${landingPageId}`, err);
        return null;
    }
}

/**
 * Bestimmt, von welcher LandingPage die Legals geladen werden sollen.
 * Logik:
 * - Wenn Recommendation vorhanden:
 *    → Falls LandingPage aktiv, Preview oder Default → landingPageID
 *    → Sonst → defaultLandingPageID
 * - Wenn keine Recommendation → Context-LandingPageID
 */
export async function getLegalLandingPageId(): Promise<string | null> {
    const recId = getRecommendationId();

    if (recId) {
        const recSnap = await getDoc(doc(db, "recommendations", recId));
        if (recSnap.exists()) {
            const recData = recSnap.data();
            const landingPageId = recData?.landingPageID;
            const defaultLandingPageId = recData?.defaultLandingPageID;

            if (landingPageId) {
                const landingPageData = await loadLandingPageData(landingPageId);
                const isActive = landingPageData?.isActive === true;
                const isDefault = landingPageData?.isDefaultPage === true;

                if (isActive || isPreviewMode() || isDefault) {
                    Logger.info("✅ Legals: Nutze landingPageID:", landingPageId);
                    return landingPageId;
                }

                if (defaultLandingPageId) {
                    Logger.warn("⚠️ LandingPage inaktiv, nutze defaultLandingPageID:", defaultLandingPageId);
                    return defaultLandingPageId;
                }
            }
        }
    }

    const fallbackId = getLandingPageId();
    if (fallbackId) {
        Logger.info("ℹ️ Legal-Fallback: Context LandingPageId:", fallbackId);
        return fallbackId;
    }

    Logger.warn("❌ Keine gültige LandingPage-ID für Legals gefunden.");
    return null;
}

/**
 * Holt ein Feld aus LandingPage (falls existiert und String)
 */
async function getLandingPageField(landingPageId: string, fieldName: string): Promise<string | null> {
    const data = await loadLandingPageData(landingPageId);
    if (!data) return null;

    const value = data[fieldName];
    if (typeof value !== "string" || value.trim().length === 0) {
        Logger.warn(`⚠️ Feld '${fieldName}' in LandingPage ${landingPageId} ist leer oder kein String.`);
        return null;
    }

    return value;
}

/**
 * Generischer Loader für Legal-Dokument-Felder
 */
async function getLegalDocumentData(fieldName: string): Promise<string | null> {
    const landingPageId = await getLegalLandingPageId();
    if (!landingPageId) return null;
    return await getLandingPageField(landingPageId, fieldName);
}

// Öffentliche API für die einzelnen Legal-Dokumente:
export async function getPrivacyPolicy(): Promise<string | null> {
    return await getLegalDocumentData("privacyPolicy");
}

export async function getImpressum(): Promise<string | null> {
    return await getLegalDocumentData("impressum");
}

export async function getInitialInfo(): Promise<string | null> {
    return await getLegalDocumentData("initialInformation");
}

export async function getTermsAndConditions(): Promise<string | null> {
    return await getLegalDocumentData("termsAndConditions");
}

export async function getScriptTags(): Promise<string | null> {
    return await getLegalDocumentData("scripts");
}

export async function getTitleName(): Promise<string | null> {
    return await getLegalDocumentData("name");
}

export async function getLandingPageTitle(landingPageId: string): Promise<string | null> {
    const snap = await getDoc(doc(db, "landingPages", landingPageId));
    if (!snap.exists()) return null;
    const data = snap.data();
    return data?.companyData?.name ?? null;
}
